import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PriorityBadge } from "@/components/Badges";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
  LineChart, Line,
} from "recharts";
import { Shield, AlertTriangle, Timer } from "lucide-react";
import CompareBadge from "@/components/reports/CompareBadge";
import { useDrillThrough } from "@/hooks/useDrillThrough";

export default function SlaPerformanceReport({ filters }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const drillTo = useDrillThrough();

  useEffect(() => {
    setLoading(true);
    const params = { ...filters };
    Object.keys(params).forEach((k) => (params[k] === "all" || params[k] === "") && delete params[k]);
    api.get("/analytics/sla", { params })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [JSON.stringify(filters)]); // eslint-disable-line

  if (loading && !data) return <div className="text-sm text-slate-400 py-8 text-center">Loading…</div>;
  if (!data) return null;

  const pct = data.kpi.sla_pct;
  const delta = data.kpi.delta_pct;
  const tone = pct == null ? "text-slate-500" : pct >= 95 ? "text-emerald-700" : pct >= 80 ? "text-amber-700" : "text-rose-700";

  return (
    <div className="space-y-5" data-testid="sla-performance-report">
      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">SLA attainment</div>
              <Shield className={`w-4 h-4 ${tone}`} />
            </div>
            <div className={`font-heading text-2xl font-bold mt-1 ${tone}`}>
              {pct != null ? `${pct}%` : "—"}
            </div>
            <div className="text-[11px] mt-1 flex items-center gap-1">
              <CompareBadge pct={delta} mode="positive-good" unit="pp" testid="sla-delta-badge" />
              {delta != null && <span className="text-slate-400">vs prev period</span>}
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">SLA breaches</div>
              <AlertTriangle className="w-4 h-4 text-rose-600" />
            </div>
            <div className="font-heading text-2xl font-bold mt-1 text-rose-700">{data.kpi.breached}</div>
            <div className="text-[11px] text-slate-500 mt-1">in period</div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Resolved</div>
              <Shield className="w-4 h-4 text-emerald-600" />
            </div>
            <div className="font-heading text-2xl font-bold mt-1 text-emerald-700">{data.kpi.resolved_count}</div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">At risk (next 60min)</div>
              <Timer className="w-4 h-4 text-amber-600" />
            </div>
            <div className="font-heading text-2xl font-bold mt-1 text-amber-700">{data.at_risk_now.length}</div>
            <div className="text-[11px] text-slate-500 mt-1">currently open</div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* FRT histogram */}
        <Card className="border-slate-200" data-testid="frt-histogram-card">
          <CardHeader className="pb-2"><CardTitle className="font-heading text-base">First-response time distribution</CardTitle></CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer>
              <BarChart data={data.frt_histogram}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: "#64748b" }} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Bar dataKey="count" fill="#4f46e5" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Resolution histogram */}
        <Card className="border-slate-200" data-testid="resolution-histogram-card">
          <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Resolution time distribution</CardTitle></CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer>
              <BarChart data={data.resolution_histogram}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: "#64748b" }} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Bar dataKey="count" fill="#059669" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Breaches by dept */}
        <Card className="border-slate-200" data-testid="breaches-by-dept-card">
          <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Breaches by department</CardTitle></CardHeader>
          <CardContent className="h-64">
            {data.breaches_by_dept.length === 0 ? (
              <div className="text-xs text-slate-400 py-8 text-center">No breaches in this window</div>
            ) : (
              <ResponsiveContainer>
                <BarChart data={data.breaches_by_dept} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} />
                  <YAxis dataKey="dept" type="category" tick={{ fontSize: 10, fill: "#64748b" }} width={120} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Bar
                    dataKey="count"
                    fill="#e11d48"
                    radius={[0, 6, 6, 0]}
                    cursor="pointer"
                    onClick={(d) => d?.dept && drillTo("department_name", d.dept, { date_from: filters.date_from, date_to: filters.date_to })}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Breaches by issue type */}
        <Card className="border-slate-200" data-testid="breaches-by-issue-card">
          <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Breaches by issue type</CardTitle></CardHeader>
          <CardContent className="h-64">
            {data.breaches_by_issue.length === 0 ? (
              <div className="text-xs text-slate-400 py-8 text-center">No breaches in this window</div>
            ) : (
              <ResponsiveContainer>
                <BarChart data={data.breaches_by_issue} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} />
                  <YAxis dataKey="issue" type="category" tick={{ fontSize: 10, fill: "#64748b" }} width={150} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Bar dataKey="count" fill="#ea580c" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* MTTR trend */}
      <Card className="border-slate-200" data-testid="mttr-trend-card">
        <CardHeader className="pb-2"><CardTitle className="font-heading text-base">MTTR (Mean Time to Resolution) — weekly</CardTitle></CardHeader>
        <CardContent className="h-56">
          {data.mttr_trend.length === 0 ? (
            <div className="text-xs text-slate-400 py-8 text-center">No resolved tickets in this window</div>
          ) : (
            <ResponsiveContainer>
              <LineChart data={data.mttr_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="week" tick={{ fontSize: 10, fill: "#64748b" }} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} label={{ value: "hours", angle: -90, position: "insideLeft", fontSize: 10, fill: "#64748b" }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v) => `${v}h`} />
                <Line type="monotone" dataKey="mttr_hours" stroke="#4f46e5" strokeWidth={2} dot={{ r: 3 }} name="MTTR" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* At-risk list */}
      <Card className="border-slate-200" data-testid="at-risk-now-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-base flex items-center gap-1.5">
            <Timer className="w-4 h-4 text-amber-600" />
            At-risk right now (next 60min)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.at_risk_now.length === 0 ? (
            <div className="text-xs text-slate-400 py-4 text-center">No tickets at risk in the next hour. Nice work.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {data.at_risk_now.map((t) => (
                <Link
                  to={`/tickets/${t.id}`} key={t.id}
                  className="flex items-center justify-between gap-3 py-2 px-1 hover:bg-slate-50 rounded group"
                  data-testid={`at-risk-row-${t.id}`}
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className="font-mono text-xs text-indigo-600 group-hover:underline">{t.code}</span>
                    <PriorityBadge value={t.priority} />
                    <span className="text-xs text-slate-600 truncate">{t.customer_name}</span>
                  </div>
                  <span className={`text-xs font-mono ${t.minutes_left < 0 ? "text-rose-600 font-bold" : "text-amber-700"}`}>
                    {t.minutes_left < 0 ? `${Math.abs(t.minutes_left)}m over` : `${t.minutes_left}m left`}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
